/**
 * PII Scrubbing Utility
 * Observability & Analytics Infrastructure - Task 1.2
 *
 * Allowlist-based PII scrubbing for Sentry and PostHog.
 * Reuses patterns from backend/src/services/audit-logger.ts hashText().
 *
 * ALLOWLIST (safe to transmit):
 * - User IDs (hashed/anonymized)
 * - Feature flags
 * - Plan type
 * - Timestamps
 * - HTTP status codes
 * - Error types
 * - Event names
 * - Audit IDs
 * - File extensions
 * - File sizes
 *
 * BLOCKLIST (always scrub):
 * - Email addresses
 * - Names (firstName, lastName, fullName)
 * - Document content
 * - Uploaded file contents
 * - Payment details
 * - IP addresses
 * - Billing addresses
 * - VAT IDs
 * - Session tokens
 */

/**
 * Field patterns that are allowed (non-PII)
 */
const ALLOWED_FIELDS = new Set([
  // IDs (should already be hashed/anonymized)
  "userId",
  "user_id",
  "auditId",
  "audit_id",
  "claimId",
  "claim_id",
  "evidenceId",
  "evidence_id",
  "id",

  // Feature flags
  "flags",
  "featureFlags",
  "feature_flags",
  "tags",

  // Plan/billing metadata (not PII)
  "planType",
  "plan_type",
  "planId",
  "plan_id",
  "subscriptionId",
  "subscription_id",

  // Timestamps
  "timestamp",
  "createdAt",
  "created_at",
  "updatedAt",
  "updated_at",
  "processedAt",
  "processed_at",

  // HTTP metadata
  "status",
  "statusCode",
  "status_code",
  "method",
  "path",
  "url",
  "request",

  // Error metadata
  "errorType",
  "error_type",
  "errorCode",
  "error_code",
  "errorMessage",
  "error_message",
  "extra",

  // Event metadata
  "eventName",
  "event_name",
  "eventType",
  "event_type",

  // File metadata (not content)
  "fileName",
  "file_name",
  "fileExtension",
  "file_extension",
  "fileSize",
  "file_size",
  "mimeType",
  "mime_type",

  // Counts/metrics
  "claimCount",
  "claim_count",
  "gapCount",
  "gap_count",
  "characterCount",
  "character_count",
  "unitCount",
  "unit_count",

  // Processing metadata
  "processingTimeMs",
  "processing_time_ms",
  "aiModelVersion",
  "ai_model_version",
  "methodologyVersion",
  "methodology_version",

  // Risk levels (non-PII)
  "riskLevel",
  "risk_level",

  // Environment
  "environment",
  "env",
  "NODE_ENV",

  // User object (will be scrubbed recursively)
  "user",
]);

/**
 * Field patterns that contain PII (block by default)
 */
const PII_FIELD_PATTERNS = [
  // Email
  /^email$/i,
  /^e_?mail$/i,
  /userEmail/i,

  // Names (but not fileName which is allowed)
  /^name$/i,
  /^first_?name$/i,
  /^last_?name$/i,
  /^full_?name$/i,
  /userName/i,

  // Document content
  /^content$/i,
  /^text$/i,
  /^document$/i,
  /^upload$/i,
  /file_?content/i,
  /documentText/i,

  // Payment details
  /\b(card(?:number|num|no|#)?|credit_?card|debit_?card|payment_?card|pan)\b/i,
  /payment/i,
  /billing/i,
  /invoice/i,
  /vat/i,
  /tax/i,

  // IP addresses (but not skip/clip etc)
  /^ip$/i,
  /ip_?address/i,

  // Tokens/secrets/keys (but allow feature flag keys). The `(^|[-_])…$`
  // suffix form also covers the backend's credential header keys -
  // x-internal-secret, x-cli-service-token, x-csrf-token - and any future
  // x-*-secret / x-*-token header without edits (H28).
  /(^|[-_])secret$/i,
  /(^|[-_])token$/i,
  /(^|[-_])key$/i,
  /apiKey$/i,
  /^password$/i,
  /sessionToken/i,

  // Credential headers (H28): matched case-insensitively so normalized
  // (lowercase), kebab-cased and Title-Cased header spellings all redact.
  // `authorization` covers every scheme (Basic/digest/opaque), not only
  // Bearer, which sanitizeValue alone would miss at the key level.
  /^cookie$/i,
  /authorization/i,

  // Address
  /address$/i,
  /street/i,
  /city/i,
  /country/i,
  /postal/i,
  /zip/i,

  // Phone
  /phone/i,
  /mobile/i,
];

/**
 * Regex patterns for detecting PII in values
 */
const PII_VALUE_PATTERNS = {
  // Email: literal @ or its percent-encoded form (%40, as produced by
  // encodeURIComponent in query strings/URLs); not RFC 5322 compliant,
  // but good enough.
  email: /\b[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/gi,

  // IP address (IPv4)
  ipv4: /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,

  // Bearer tokens
  bearerToken: /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi,

  // API keys (specific known prefixes)
  apiKey: /\b(?:sk_live_|sk_test_|pk_live_|pk_test_)[A-Za-z0-9]{16,}\b/gi,

  // Credit card (simple pattern)
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
};

/**
 * Check if a field name is a feature flag key
 */
function isFeatureFlagKey(key: string): boolean {
  return key.startsWith("FF_") || key.startsWith("VITE_FF_");
}

/**
 * Check if a field name contains PII
 */
export function isPIIField(key: string): boolean {
  // Allow explicitly allowlisted fields
  if (ALLOWED_FIELDS.has(key)) {
    return false;
  }

  // Allow feature flag keys
  if (isFeatureFlagKey(key)) {
    return false;
  }

  // Block if matches PII pattern
  return PII_FIELD_PATTERNS.some((pattern) => pattern.test(key));
}

/**
 * Sanitize a value by removing PII patterns
 */
export function sanitizeValue(value: unknown): unknown {
  if (typeof value === "string") {
    let sanitized = value;

    // Replace email addresses
    sanitized = sanitized.replace(PII_VALUE_PATTERNS.email, "[EMAIL_REDACTED]");

    // Replace IP addresses
    sanitized = sanitized.replace(PII_VALUE_PATTERNS.ipv4, (match) => {
      const octets = match.split(".").map((part) => Number(part));
      const isValid = octets.length === 4 &&
        octets.every((octet) =>
          Number.isInteger(octet) && octet >= 0 && octet <= 255
        );
      return isValid ? "[IP_REDACTED]" : match;
    });

    // Replace bearer tokens
    sanitized = sanitized.replace(
      PII_VALUE_PATTERNS.bearerToken,
      "Bearer [TOKEN_REDACTED]",
    );

    // Replace API keys (32+ char alphanumeric)
    sanitized = sanitized.replace(PII_VALUE_PATTERNS.apiKey, "[KEY_REDACTED]");

    // Replace credit card numbers
    sanitized = sanitized.replace(
      PII_VALUE_PATTERNS.creditCard,
      "[CARD_REDACTED]",
    );

    return sanitized;
  }

  return value;
}

/**
 * Recursively scrub PII from an object
 * Uses allowlist approach: keep only explicitly allowed fields
 *
 * @param obj Object to scrub
 * @returns Scrubbed object
 */
export function scrubPII(obj: unknown): unknown {
  // Handle primitives
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== "object") {
    return sanitizeValue(obj);
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => scrubPII(item));
  }

  // Handle objects
  const scrubbed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Skip PII fields entirely
    if (isPIIField(key)) {
      scrubbed[key] = "[PII_REDACTED]";
      continue;
    }

    // Recursively scrub nested objects
    if (value !== null && typeof value === "object") {
      scrubbed[key] = scrubPII(value);
    } else {
      scrubbed[key] = sanitizeValue(value);
    }
  }

  return scrubbed;
}

/**
 * Hash text using SHA-256
 * Reuses pattern from backend/src/services/audit-logger.ts
 *
 * @param text Text to hash
 * @returns Hex-encoded SHA-256 hash
 */
export async function hashText(text: string): Promise<string> {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error(
      "Web Crypto API is not available. Ensure a secure context or provide a Node.js fallback.",
    );
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
